import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { getHostname, rangeCreator, ScrolledToEndDirective } from '@client-monorepo/common/utilities';
import { map, Subject, takeUntil } from 'rxjs';
import {
  ProductInterface,
  ProductPreviewComponent,
  ProductsSortAndFilterComponent,
  ProductsSortAndFilterService,
  StoresApiService,
} from '@client-monorepo/daily-fintech/stores';
import { NoItemComponent, PageLayoutComponent } from '@client-monorepo/common/ui-components';
import { ActivatedRoute } from '@angular/router';
import { ProductListPageConfig } from '../../data-access/constants/plp-config';
import { AnalyticsEventType, EventManagementService } from '@client-monorepo/common/event-management';
import { RequestTypeEnum, RestrictionTypes } from '@client-monorepo/common/network';
import {
  PromotionApiService,
  PromotionGroupInterface,
  PromotionGroupTypeEnum,
  PromotionItemRestrictionEnum,
} from '@client-monorepo/common/promotions';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NgxSearchBoxComponent } from '@digipay/ngx-search-box';
import { ActionHandlerService, ActionType, RedirectionTypeEnum } from '@client-monorepo/common/action-handler';
import { ProductsListService } from '../../data-access/services/products-list-service';

@Component({
  selector: 'stores-applet-products-list',
  standalone: true,
  imports: [
    CommonModule,
    PageLayoutComponent,
    ProductPreviewComponent,
    ScrolledToEndDirective,
    NoItemComponent,
    ProductsSortAndFilterComponent,
    NgxSearchBoxComponent,
  ],
  templateUrl: './products-list.component.html',
  styleUrl: './products-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsListComponent implements OnInit {
  config = computed(() => this.productListService.config());
  products = computed(() => this.productListService.products());
  productsLoading = computed(() => this.productListService.productsLoading());

  rangeCreator = rangeCreator;

  productListService = inject(ProductsListService);
  activatedRoute = inject(ActivatedRoute);
  destroyRef = inject(DestroyRef);

  constructor() {
    toObservable(this.productListService.sort)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.productListService.initialized()) {
            this.productListService.reloadProducts();
          }
        },
      });
    toObservable(this.productListService.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.productListService.initialized()) {
            this.productListService.reloadProducts();
          }
        },
      });
  }

  ngOnInit(): void {
    this.getQueryParam();
    this.productListService.resetSortAndFilter();
    this.productListService.initializePageConfig();
  }

  getQueryParam(): void {
    const promotionId = this.activatedRoute.snapshot.paramMap.get('promotionId') || '';
    const storeTitle = this.activatedRoute.snapshot.paramMap.get('title') || '';
    const minDiscount = this.activatedRoute.snapshot.paramMap.get('minDiscount') || '';
    if (promotionId) {
      this.productListService.promotionId.set(promotionId);
    } else if (storeTitle) {
      this.productListService.storeTitle = decodeURI(storeTitle) as string;
    } else if (minDiscount) {
      const maxDiscount = this.activatedRoute.snapshot.paramMap.get('maxDiscount') || '';
      const category = this.activatedRoute.snapshot.paramMap.get('category') || '';
      this.productListService.discountConfig.set({
        min: +minDiscount,
        max: +maxDiscount,
        aiCategory: category,
      });
    }
  }

  loadNewPage(): void {
    this.productListService.loadNewPage();
  }

  goToProductPage(product: ProductInterface) {
    this.productListService.goToProductPage(product);
  }

  searchEnd(event: any) {
    this.productListService.searchEnd(event);
  }
}
